import { useState, useMemo, useEffect } from "react";
import { propertiesApi } from "../../lib/propertiesApi";
import { analyticsApi } from "../../lib/analyticsApi";
import { useNavigate } from "react-router-dom";
import { useAppData } from "../../lib/AppDataContext";
import { Card } from "../../components/shared/Card";
import { Modal } from "../../components/shared/Modal";
import { PropertyForm } from "./PropertyForm";
import { PropertyWizard } from "./PropertyWizard";
import { toast } from "../../components/shared/Toast";

const STATUS_STYLES = {
  draft:     "bg-slate-100 text-slate-700 border-slate-200",
  published: "bg-emerald-50 text-emerald-700 border-emerald-200",
  archived:  "bg-amber-50 text-amber-700 border-amber-200",
};

const STATUS_LABELS = { draft: "Draft", published: "Published", archived: "Archived" };
const TYPE_LABELS = { apartment: "Apartment", villa: "Villa", plot: "Plot", duplex: "Duplex", studio: "Studio Apartment", commercial: "Commercial" };
const POSSESSION_LABELS = { ready_to_move: "Ready to Move", nearing_possession: "Nearing Possession", under_construction: "Under Construction", new_launch: "New Launch" };
const POSSESSION_STYLES = {
  ready_to_move:      "bg-blue-50 text-blue-700 border-blue-200",
  nearing_possession: "bg-violet-50 text-violet-700 border-violet-200",
  under_construction: "bg-amber-50 text-amber-700 border-amber-200",
  new_launch:         "bg-teal-50 text-teal-700 border-teal-200",
};
const PROPERTY_TYPE_OPTIONS = [
  { value: "apartment", label: "Apartment" }, { value: "villa", label: "Villa" },
  { value: "plot", label: "Plot" }, { value: "duplex", label: "Duplex" },
  { value: "studio", label: "Studio Apartment" }, { value: "commercial", label: "Commercial" },
];

export function PropertiesView({ view = "all", startAdding = false } = {}) {
  const {
    properties, propertiesLoading,
    localities, builders, mastersLoading,
    addProperty, updateProperty, deleteProperty, duplicateProperty,
    refreshProperties,
  } = useAppData();
  const nav = useNavigate();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [localityFilter, setLocalityFilter] = useState("all");
  const [builderFilter, setBuilderFilter] = useState("all");
  const [adding, setAdding] = useState(startAdding);
  const [editing, setEditing] = useState(null);
  // Edit must load the FULL property (the list row is a trimmed projection
  // missing many fields). Fetch by id, then open the wizard with complete data.
  const openEdit = async (row) => {
    try {
      // getOne, not getById — the latter does not exist on propertiesApi, so
      // this threw a TypeError before any request was made. The bare catch
      // below swallowed it and the wizard silently opened with the list row,
      // which carries no images[] (the list query does not include the
      // relation). That is why the Media step showed no gallery while the
      // customer site showed every image.
      const full = await propertiesApi.getOne(row.id);
      setEditing(full || row);
    } catch (e) {
      // Log it. A silent fallback here hid the bug above for a long time.
      console.error("[properties] full fetch failed, opening with list row", e);
      setEditing(row);
    }
  };
  const [deleting, setDeleting] = useState(null);

  // Real per-property Views + Inquiries from the analytics endpoint
  // (pageViews matched to /project/<slug>, and leads grouped by propertyId).
  // Keyed by property id. Fails soft: on error the map stays empty and rows
  // show 0 — a truthful value, never a mock number.
  const [statsById, setStatsById] = useState({});
  useEffect(() => {
    let alive = true;
    analyticsApi.topProperties()
      .then((rows) => {
        if (!alive || !Array.isArray(rows)) return;
        const map = {};
        for (const r of rows) map[r.id] = { views: r.views || 0, inquiries: r.inquiries || 0 };
        setStatsById(map);
      })
      .catch(() => { /* leave empty -> rows show 0 */ });
    return () => { alive = false; };
  }, []);

  // The sidebar drives `view` — "all" (all statuses), "new" (recently
  // created, currently maps to draft + published), or "drafts" (draft only).
  // The auto-filter applies BEFORE the user's manual filters in the toolbar,
  // so "Drafts" never shows Published items even if the user clears filters.
  const viewBaseFilter = useMemo(() => {
    if (view === "drafts") return (p) => p.status === "draft";
    if (view === "new") return (p) => {
      // "New" = recently created (last 14 days), any status.
      const created = new Date(p.createdAt || p.publishedAt || 0);
      const age = Date.now() - created.getTime();
      return age < 14 * 24 * 60 * 60 * 1000;
    };
    return null; // "all" — no auto-filter
  }, [view]);

  const filteredProps = useMemo(() => {
    return properties.filter((p) => {
      if (viewBaseFilter && !viewBaseFilter(p)) return false;
      if (search && !(p.name.toLowerCase().includes(search.toLowerCase()) || p.slug.includes(search.toLowerCase()))) return false;
      if (statusFilter !== "all" && p.status !== statusFilter) return false;
      if (typeFilter !== "all" && p.type !== typeFilter) return false;
      if (localityFilter !== "all" && p.localitySlug !== localityFilter) return false;
      if (builderFilter !== "all" && p.builderSlug !== builderFilter) return false;
      return true;
    });
  }, [properties, search, statusFilter, typeFilter, localityFilter, builderFilter, viewBaseFilter]);

  async function handleToggleFeatured(p) {
    try {
      await updateProperty(p.id, { featured: !p.featured });
      toast(p.featured ? "Removed from featured" : "Marked as featured");
    } catch (err) {
      toast(err?.message || "Failed");
    }
  }

  async function handleDuplicate(p) {
    try {
      await duplicateProperty(p.id);
      toast("Property duplicated");
    } catch (err) {
      toast(err?.message || "Failed to duplicate");
    }
  }

  async function handleDelete() {
    if (!deleting) return;
    try {
      await deleteProperty(deleting.id);
      toast("Property deleted");
      setDeleting(null);
    } catch (err) {
      toast(err?.message || "Failed to delete");
    }
  }

  if (propertiesLoading || mastersLoading) {
    return (
      <div className="p-6">
        <Card>
          <div className="p-10 text-center text-slate-500 text-sm">Loading properties…</div>
        </Card>
      </div>
    );
  }

  // Full-page wizard — when adding or editing, the wizard REPLACES the list
  // (it has its own header + Cancel). Returns to the list on cancel/save.
  if (adding || editing) {
    return (
      <PropertyWizard
        initial={editing}
        onCancel={() => { setAdding(false); setEditing(null); }}
        onSaved={async () => {
          await refreshProperties();
          setAdding(false);
          setEditing(null);
        }}
      />
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">
            {view === "drafts" ? "Draft Properties" : view === "new" ? "New Properties" : "Properties"}
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            {filteredProps.length} of {properties.length} shown
            {view === "all" && ` · ${properties.filter((p) => p.status === "published").length} published`}
            {view === "drafts" && ` · awaiting publish`}
            {view === "new" && ` · created in last 14 days`}
          </p>
        </div>
        <button
          onClick={() => setAdding(true)}
          className="px-4 py-2 bg-navy text-white rounded text-sm font-medium hover:bg-navy/90"
        >
          + Add property
        </button>
      </div>

      <Card>
        <div className="p-4 border-b border-slate-200 flex flex-wrap gap-2 items-center">
          <input
            placeholder="Search by name or slug…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 min-w-[160px] px-3 py-1.5 border border-slate-300 rounded text-sm"
          />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded text-sm">
            <option value="all">All status</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded text-sm">
            <option value="all">All types</option>
            {PROPERTY_TYPE_OPTIONS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <select value={localityFilter} onChange={(e) => setLocalityFilter(e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded text-sm">
            <option value="all">All localities</option>
            {localities.filter((l) => l.active).map((l) => (
              <option key={l.id} value={l.slug}>{l.name}</option>
            ))}
          </select>
          <select value={builderFilter} onChange={(e) => setBuilderFilter(e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded text-sm">
            <option value="all">All builders</option>
            {builders.filter((b) => b.active).map((b) => (
              <option key={b.id} value={b.slug}>{b.name}</option>
            ))}
          </select>
        </div>

        <div className="overflow-auto max-h-[70vh]">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="sticky top-0 z-20 text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-4 py-2 font-semibold">Project</th>
                <th className="px-4 py-2 font-semibold">Type</th>
                <th className="px-4 py-2 font-semibold">Builder</th>
                <th className="px-4 py-2 font-semibold">Locality</th>
                <th className="px-4 py-2 font-semibold">Price</th>
                <th className="px-4 py-2 font-semibold">Status</th>
                <th className="px-4 py-2 font-semibold text-right">Views</th>
                <th className="px-4 py-2 font-semibold text-right">Inquiries</th>
                <th className="px-4 py-2 font-semibold text-right"></th>
              </tr>
            </thead>
            <tbody>
              {filteredProps.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400 text-sm">No properties match.</td></tr>
              )}
              {filteredProps.map((p) => (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleFeatured(p)}
                        title={p.featured ? "Unfeature" : "Feature"}
                        className={p.featured ? "text-amber-500" : "text-slate-300 hover:text-amber-400"}
                      >
                        {p.featured ? "★" : "☆"}
                      </button>
                      <span className="font-medium text-slate-900">{p.name}</span>
                      {p.isDemo && <span className="text-[10px] uppercase tracking-wider text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">Demo</span>}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 ml-6">/{p.slug}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-block px-2 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-600 text-xs font-medium">{TYPE_LABELS[p.type] || p.type || "—"}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{p.builder || <span className="text-slate-400">—</span>}</td>
                  <td className="px-4 py-3 text-slate-700">{p.localityArea ? `${p.localityArea}` : <span className="text-slate-400">—</span>}</td>
                  <td className="px-4 py-3 text-slate-700 tabular-nums">{p.priceDisplay || <span className="text-slate-400">—</span>}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-1">
                      <span className={`inline-block px-2 py-0.5 rounded border text-xs font-medium ${STATUS_STYLES[p.status] || ""}`}>
                        {STATUS_LABELS[p.status] || p.status}
                      </span>
                      {p.possessionStatus && POSSESSION_LABELS[p.possessionStatus] && (
                        <span className={`inline-block px-2 py-0.5 rounded border text-xs font-medium ${POSSESSION_STYLES[p.possessionStatus] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
                          {POSSESSION_LABELS[p.possessionStatus]}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right text-slate-600 tabular-nums">{(statsById[p.id]?.views ?? 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-slate-600 tabular-nums">{statsById[p.id]?.inquiries ?? 0}</td>
                  <td className="px-4 py-3 text-right space-x-1 whitespace-nowrap">
                    <button onClick={() => openEdit(p)} className="text-xs text-navy hover:underline">Edit</button>
                    <span className="text-slate-300">·</span>
                    <button onClick={() => handleDuplicate(p)} className="text-xs text-slate-600 hover:underline">Duplicate</button>
                    <span className="text-slate-300">·</span>
                    <button onClick={() => setDeleting(p)} className="text-xs text-red-600 hover:underline">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>


      {deleting && (
        <Modal onClose={() => setDeleting(null)} title="Delete property?" size="sm">
          <div className="p-4 space-y-3 text-sm">
            <p>Soft-delete <strong>{deleting.name}</strong>?</p>
            <p className="text-xs text-slate-500">Property is moved to archive and hidden from the customer site. It can be restored from the DB but not from the admin UI (Phase 4).</p>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setDeleting(null)} className="px-3 py-1.5 text-xs border border-slate-300 rounded hover:bg-slate-50">Cancel</button>
              <button onClick={handleDelete} className="px-3 py-1.5 text-xs bg-red-600 text-white rounded hover:bg-red-700">Delete</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
